import type { ProductResponse } from "../../admin/products/productsApi";
import { useProducts } from "../../admin/products/useProducts";
import type { Currency } from "../../data/BaseEntity";
import type { ECommerceOfferProductWithNames, ECommerceOfferResponse } from "../../eCommerce/eCommerceApi";
import { Price } from "../../misc/price";
import { getExchangeRate } from "../../orders/orderEdit/getExchangeRate";

export interface EcommerceProductWithOptions extends Omit<ECommerceOfferProductWithNames, "price"> {
  price: Price;
  exchangeRate: number | undefined;
}

function toProduct(
  offerProduct: ECommerceOfferProductWithNames,
  currency: Currency,
  currencyExchangeRate: number | undefined,
  availableProducts: ProductResponse[],
): EcommerceProductWithOptions {
  const matchingProduct = availableProducts.find((p) => p.id === offerProduct.id);
  return {
    ...offerProduct,
    price: new Price(offerProduct.price ?? "UNAVAILABLE"),
    exchangeRate: getExchangeRate(offerProduct.currency, currency, currencyExchangeRate ?? undefined),
    options: offerProduct.options.map((o) => {
      const matchingOption = matchingProduct?.options.find((op) => op.id === o.id);
      return {
        ...o,
        info: matchingOption?.info,
        order: matchingOption?.order,
      };
    }),
  };
}

export function useECommerceOfferToProducts(
  offer: Pick<ECommerceOfferResponse, "products" | "currency" | "currencyExchangeRate"> | null | undefined,
): { isLoading: boolean; products: EcommerceProductWithOptions[] } {
  const { products, isLoading } = useProducts();
  if (!offer) {
    return {
      isLoading,
      products: [],
    };
  }
  return {
    isLoading,
    products: offer.products.map((p) => toProduct(p, offer.currency, offer.currencyExchangeRate, products)),
  };
}
